import { useState, useMemo } from "react";
import {
  useDiscosList,
  useCreateDisco,
  useUpdateDisco,
  useDeleteDisco,
} from "../../../api/bill";
import { useAdminApiProviders } from "../../../api/admin";
import { toast } from "react-hot-toast";

// Backend response types
interface BackendDisco {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  disco_id: string;
  is_active: boolean;
  commission: string | null;
  provider: number;
}

interface BackendProvider {
  id: number;
  name: string;
  is_active: boolean;
}

interface Disco {
  id: number;
  name: string;
  discoId: string;
  isActive: boolean;
  commission: number | null;
  provider: number;
  providerName: string;
  createdAt: string;
}

export default function ElectricityManagement() {
  // API Hooks
  const {
    data: discosData,
    isLoading: discosLoading,
    refetch: refetchDiscos,
  } = useDiscosList();
  const { data: providersResponse } = useAdminApiProviders();

  const createDisco = useCreateDisco();
  const updateDisco = useUpdateDisco();
  const deleteDisco = useDeleteDisco();

  // Local State
  const [showModal, setShowModal] = useState(false);
  const [editingDisco, setEditingDisco] = useState<Disco | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    disco_id: "",
    commission: "",
    provider: "",
    is_active: true,
  });

  // Extract providers from nested response
  const providers = useMemo(() => {
    if (!providersResponse?.results?.data) return [];
    return providersResponse.results.data as BackendProvider[];
  }, [providersResponse]);

  // Map discos
  const discos = useMemo(() => {
    if (!discosData || !Array.isArray(discosData)) return [];

    return (discosData as BackendDisco[]).map((disco) => {
      const provider = providers.find((p) => p.id === disco.provider);

      return {
        id: disco.id,
        name: disco.name,
        discoId: disco.disco_id,
        isActive: disco.is_active,
        commission: disco.commission ? parseFloat(disco.commission) : null,
        provider: disco.provider,
        providerName: provider?.name || `Provider ${disco.provider}`,
        createdAt: new Date(disco.created_at).toLocaleDateString(),
      };
    });
  }, [discosData, providers]);

  // Filter discos
  const filteredDiscos = useMemo(() => {
    return discos.filter(
      (disco) =>
        disco.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        disco.discoId.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [discos, searchTerm]);

  // CRUD handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        disco_id: formData.disco_id,
        commission: formData.commission || null,
        provider: parseInt(formData.provider),
        is_active: formData.is_active,
      };

      if (editingDisco) {
        await updateDisco.mutateAsync({
          id: editingDisco.id,
          data: payload,
        });
        toast.success("Electricity provider updated successfully!");
      } else {
        await createDisco.mutateAsync(payload);
        toast.success("Electricity provider created successfully!");
      }

      refetchDiscos();
      resetForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save electricity provider";
      toast.error(errorMessage);
      console.error("Error saving disco:", error);
    }
  };

  const handleEdit = (disco: Disco) => {
    setEditingDisco(disco);
    setFormData({
      name: disco.name,
      disco_id: disco.discoId,
      commission: disco.commission?.toString() || "",
      provider: disco.provider.toString(),
      is_active: disco.isActive,
    });
    setShowModal(true);
  };

  const handleDelete = async (discoId: number) => {
    if (!confirm("Are you sure you want to delete this electricity provider?"))
      return;

    try {
      await deleteDisco.mutateAsync(discoId);
      toast.success("Electricity provider deleted successfully!");
      refetchDiscos();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete electricity provider";
      toast.error(errorMessage);
      console.error("Error deleting disco:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      disco_id: "",
      commission: "",
      provider: "",
      is_active: true,
    });
    setEditingDisco(null);
    setShowModal(false);
  };

  // Loading state
  if (discosLoading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-red-600 animate-spin"></i>
            <p className="mt-2 text-gray-600">
              Loading electricity providers...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Electricity Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage electricity distribution companies (DISCOs)
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="mt-4 md:mt-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-fill mr-2"></i>
          Add DISCO
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Providers
          </label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or DISCO ID"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredDiscos.length === 0 && (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <i className="ri-flashlight-line text-5xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Electricity Providers Found
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? "Try adjusting your search terms"
              : "Get started by adding your first electricity provider"}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <i className="ri-add-fill mr-2"></i>
              Add DISCO
            </button>
          )}
        </div>
      )}

      {/* Providers Table */}
      {filteredDiscos.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DISCO ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDiscos.map((disco) => (
                  <tr key={disco.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <i className="ri-flashlight-fill text-yellow-600 text-lg"></i>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {disco.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {disco.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                        {disco.discoId}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {disco.providerName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {disco.commission ? `${disco.commission}%` : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          disco.isActive
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {disco.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {disco.createdAt}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(disco)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <i className="ri-edit-fill"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(disco.id)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <i className="ri-delete-bin-fill"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingDisco
                    ? "Edit Electricity Provider"
                    : "Add New Provider"}
                </h3>
                <button
                  onClick={resetForm}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provider Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., Ikeja Electricity"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DISCO ID
                  </label>
                  <input
                    type="text"
                    value={formData.disco_id}
                    onChange={(e) =>
                      setFormData({ ...formData, disco_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    placeholder="e.g., 1"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Provider
                  </label>
                  <select
                    value={formData.provider}
                    onChange={(e) =>
                      setFormData({ ...formData, provider: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    required
                  >
                    <option value="">Select Provider</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission (%)
                  </label>
                  <input
                    type="number"
                    value={formData.commission}
                    onChange={(e) =>
                      setFormData({ ...formData, commission: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="Optional"
                  />
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label
                    htmlFor="is_active"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active Provider
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    disabled={createDisco.isPending || updateDisco.isPending}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createDisco.isPending || updateDisco.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        {editingDisco ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>
                        {editingDisco ? "Update Provider" : "Create Provider"}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
